import {
  TROPHY_TIER_IDS,
  type GamificationEventType,
  type GamificationHeroEvent as HeroEventDto,
} from '@crowdfpl/domain/gamification';
import { Badge } from '@crowdfpl/ui-web/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@crowdfpl/ui-web/card';
import { Award, Sparkles, Trophy } from 'lucide-react';
import { type ReactElement } from 'react';
import { cn } from '@/helpers/utils';

type HeroEvent = NonNullable<HeroEventDto>;

type GamificationHeroEventCardProps = {
  event: HeroEvent;
  gameweekNumber?: number | null;
  evidenceSummary?: string | null;
  className?: string;
};

function formatIdentityLabel(value: string): string {
  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function eventIcon(eventType: GamificationEventType): ReactElement {
  if (eventType === 'progress_tier_unlocked') {
    return <Sparkles className="h-5 w-5 text-primary" aria-hidden />;
  }
  if (eventType === 'trophy_one_shot_unlocked') {
    return <Award className="h-5 w-5 text-primary" aria-hidden />;
  }
  return <Trophy className="h-5 w-5 text-primary" aria-hidden />;
}

function tierLabel(tierIndex: number | null): string | null {
  if (tierIndex == null || tierIndex < 0 || tierIndex >= TROPHY_TIER_IDS.length) {
    return null;
  }
  return formatIdentityLabel(TROPHY_TIER_IDS[tierIndex]);
}

/**
 * Hero card for the latest status-changing gamification event.
 * Rendered from server `updates.hero_event` only — never from a generic notification feed.
 */
export function GamificationHeroEvent({
  event,
  gameweekNumber = null,
  evidenceSummary = null,
  className,
}: GamificationHeroEventCardProps): ReactElement | null {
  const trophyLabel = event.trophy_id ? formatIdentityLabel(event.trophy_id) : null;
  const unlockedTierLabel = tierLabel(event.tier_index);

  return (
    <Card
      data-testid="gamification-hero-event"
      className={cn(
        'border-primary/30 bg-card shadow-none',
        'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-300',
        'motion-reduce:animate-none motion-reduce:transition-none',
        className,
      )}
    >
      <CardHeader className="space-y-2.5 p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10',
              'motion-safe:transition-transform motion-reduce:transition-none',
            )}
            aria-hidden
          >
            {eventIcon(event.event_type)}
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <CardTitle className="text-base font-semibold leading-snug tracking-tight text-foreground">
              {event.title}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground">{event.body}</CardDescription>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pl-[3.25rem]">
          {gameweekNumber != null && gameweekNumber > 0 ? (
            <Badge variant="secondary" className="font-normal">
              GW {gameweekNumber}
            </Badge>
          ) : null}
          {trophyLabel ? (
            <Badge variant="outline" className="font-normal">
              {trophyLabel}
            </Badge>
          ) : null}
          {unlockedTierLabel ? (
            <Badge variant="outline" className="font-normal">
              {unlockedTierLabel}
            </Badge>
          ) : null}
          {event.event_type === 'progress_tier_unlocked' ? (
            <Badge variant="outline" className="font-normal">
              Progress tier
            </Badge>
          ) : null}
          {event.event_type === 'trophy_one_shot_unlocked' ? (
            <Badge variant="outline" className="font-normal">
              One-shot
            </Badge>
          ) : null}
        </div>
        {evidenceSummary ? (
          <p
            className="pl-[3.25rem] text-xs text-muted-foreground"
            data-testid="gamification-hero-evidence"
          >
            {evidenceSummary}
          </p>
        ) : null}
      </CardHeader>
    </Card>
  );
}
